import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo8() {
  const pdfRef = useRef();

  const data = [
    { year: 2018, female: 35235, male: 81611 },
    { year: 2019, female: 37261, male: 83532 },
    { year: 2020, female: 36896, male: 82614 },
    { year: 2021, female: 40558, male: 87598 },
    { year: 2022, female: 43733, male: 91885 },
    { year: 2023, female: 43660, male: 91910 },
    { year: 2024, female: 44039, male: 92941 },
  ];

  // Chart Data
  const chartData = {
    labels: data.map((d) => d.year),
    plugins: {
      datalabels: {
        display: false,
      },
    },
    datasets: [
      {
        label: "Female",
        data: data.map((d) => d.female),
        backgroundColor: "#EC4899",
        borderRadius: 6,
      },
      {
        label: "Male",
        data: data.map((d) => d.male),
        backgroundColor: "#0ea5e9",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment Trend by Gender (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Number of Employees" },
      },
    },
  };

  // Summary Insights
  const firstYear = data[0].female + data[0].male;
  const lastYear = data[data.length - 1].female + data[data.length - 1].male;
  const growthPercent = (((lastYear - firstYear) / firstYear) * 100).toFixed(1);

  const topYear = data.reduce(
    (max, item) =>
      item.female + item.male > max.total
        ? { year: item.year, total: item.female + item.male }
        : max,
    { year: data[0].year, total: data[0].female + data[0].male }
  );

  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("employment_trend.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-lg"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Trend by Gender (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-blue-800">+{growthPercent}%</p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Peak Year</p>
            <p className="text-xl font-bold text-yellow-800">{topYear.year}</p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              2024 Total Employment
            </p>
            <p className="text-xl font-bold text-green-800">
              {lastYear.toLocaleString("en-US")} 👷
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col h-full">
            <div className="overflow-x-auto flex-grow">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    <th className="p-2">Female</th>
                    <th className="p-2">Male</th>
                    <th className="p-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${
                        row.year === 2024
                          ? "bg-green-100 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2">{row.year}</td>
                      <td className="p-2">
                        {row.female.toLocaleString("en-US")}
                      </td>
                      <td className="p-2">
                        {row.male.toLocaleString("en-US")}
                      </td>
                      <td className="p-2">
                        {(row.female + row.male).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
